/**
 * Ajout AGORA (absent du bundle de référence) – messages éphémères (« toasts ») sur Radix Toast : confirmation brève
 * d'une action (« Vue par défaut enregistrée »), avertissement non bloquant.
 * Comportement Radix : annonce aux lecteurs d'écran (poli ; « danger » = immédiat), disparition automatique (5 s par
 * défaut) suspendue au survol, au focus et quand la fenêtre n'est pas au premier plan, F8 pour atteindre la zone des
 * messages, Échap ou glisser pour fermer. Les écrans n'utilisent pas ces composants directement mais
 * `useMessageEphemere()` du socle (zone unique montée par le socle). Textes fournis traduits par l'appelant.
 * Un message éphémère ne remplace ni une erreur de saisie (à côté du champ) ni une notification (dock › cloche).
 */
import React from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { cx, h, Icon, type IconName } from './base';

export type StatutToast = 'success' | 'info' | 'danger';

const ICONE: Record<StatutToast, IconName> = { success: 'check', info: 'info', danger: 'alerte' };

export interface ToastProps {
  title: string; description?: string; status?: StatutToast;
  /** Action facultative (ex. « Annuler ») : `altText` dit comment faire sans la souris (exigence Radix). */
  action?: { label: string; altText: string; onClick: () => void };
  closeLabel: string;
  /** Durée d'affichage en ms (5 000 par défaut ; plus longue pour un message avec action). */
  duration?: number;
  open?: boolean; onOpenChange?: (open: boolean) => void;
}

export function Toast(p: ToastProps) {
  const statut = p.status || 'info';
  return h(RadixToast.Root, {
    className: cx('agx-toast', 'agx-toast-' + statut, 'ag-glass', 'ag-glass-strong'), open: p.open, onOpenChange: p.onOpenChange,
    duration: p.duration, type: statut === 'danger' ? 'foreground' : 'background',
  },
  h('span', { className: 'agx-toast-icone', 'aria-hidden': true }, h(Icon, { name: ICONE[statut], size: 16 })),
  h('div', { className: 'agx-toast-texte' },
    h(RadixToast.Title, { className: 'agx-toast-titre' }, p.title),
    p.description ? h(RadixToast.Description, { className: 'agx-toast-description' }, p.description) : null),
  p.action ? h(RadixToast.Action, { asChild: true, altText: p.action.altText },
    h('button', { type: 'button', className: 'ag-btn ag-btn-quiet ag-btn-sm', onClick: p.action.onClick }, p.action.label)) : null,
  h(RadixToast.Close, { asChild: true },
    h('button', { type: 'button', className: 'ag-page-x', 'aria-label': p.closeLabel, title: p.closeLabel }, h(Icon, { name: 'close', size: 14 }))));
}

export interface ToastZoneProps {
  children?: React.ReactNode;
  /** Nom de la zone pour les lecteurs d'écran, ex. « Messages (F8) » (traduit). */
  label: string;
  /** Nom de chaque message pour les lecteurs d'écran, ex. « Message » (traduit). */
  itemLabel: string;
}

/** Zone des messages (en bas à droite ; en haut, pleine largeur, sur mobile). */
export function ToastZone(p: ToastZoneProps) {
  return h(RadixToast.Provider, { label: p.itemLabel, swipeDirection: 'right', duration: 5000 },
    p.children,
    h(RadixToast.Viewport, { className: 'agx-toasts', label: p.label }));
}
